import { Injectable } from '@angular/core';
import { AbstractMsRef, BulkJobStatus, MicrosoftBulkJob } from '../models/generated/ziroModels';
import { PollingAbstractService } from '../services/abstract/polling.abstract.service';
import { Observable } from 'rxjs';
import { delayWhen, map } from 'rxjs/operators';

@Injectable()
export class MicrosoftBulkProvisioningPollingService extends PollingAbstractService<MicrosoftBulkJob[]> {
  baseUrl = '/services/microsoft/automate-jobs/tenants';
  msBulkJobsStr = 'microsoft-bulk-jobs';

  private _hasInitialCheck = false;

  post(tenantId: number, body: MicrosoftBulkJob): Observable<AbstractMsRef> {
    return this._http
      .post<AbstractMsRef>(`${this.baseUrl}/${tenantId}/${this.msBulkJobsStr}`, body)
      .pipe(delayWhen(() => this.refresh(tenantId)));
  }

  put(tenantId: number, jobId: string, body: MicrosoftBulkJob): Observable<void> {
    return this._http
      .put<void>(`${this.baseUrl}/${tenantId}/${this.msBulkJobsStr}/${jobId}`, body)
      .pipe(delayWhen(() => this.refresh(tenantId)));
  }

  delete(tenantId: number, draftId: any): Observable<void> {
    return this._http
      .delete<void>(`${this.baseUrl}/${tenantId}/${this.msBulkJobsStr}/${draftId}`)
      .pipe(delayWhen(() => this.refresh(tenantId)));
  }

  refresh(tenantId: number): Observable<void> {
    return this._getJobs(tenantId).pipe(
      map((data: MicrosoftBulkJob[]) => {
        this._stateSource.next(data);
      }),
    );
  }

  startConditionalPolling(tenantId: number): void {
    if (this._hasInitialCheck || this.getIsPolling()) {
      return;
    }

    this._hasInitialCheck = true;

    this._getJobs(tenantId).subscribe((jobs: MicrosoftBulkJob[]) => {
      const inProgressJobs = jobs.filter((job) => job.status === BulkJobStatus.IN_PROGRESS);
      this._stateSource.next(jobs);
      if (inProgressJobs.length > 0) {
        const newBaseUrl = this.reconstructBaseUrl(tenantId);
        this.startPollingIfStopped(0, newBaseUrl);
      }
    });
  }

  stopPolling(): void {
    super.stopPolling();
    this._hasInitialCheck = false;
  }

  run(tenantId: number, jobId: string): Observable<void> {
    return this._http
      .post<void>(`${this.baseUrl}/${tenantId}/${this.msBulkJobsStr}/${jobId}/run`, null)
      .pipe(delayWhen(() => this.refresh(tenantId)));
  }

  reconstructBaseUrl = (tenantId: number): string => {
    return `${this.baseUrl}/${tenantId}/${this.msBulkJobsStr}`;
  };

  private _getJobs(tenantId: number): Observable<MicrosoftBulkJob[]> {
    return this._http.get<MicrosoftBulkJob[]>(`${this.baseUrl}/${tenantId}/${this.msBulkJobsStr}`);
  }
}
